/**
 * porto.js - DOCA 4, terminal de carga.
 *
 * IDENTIDADE: o mapa e feito de CONTEINER. A planta nao tem rua nem quarteirao:
 * tem um patio de estiva com cinco fileiras de pilhas alinhadas leste-oeste,
 * cortadas por duas faixas de caminhao norte-sul. Isso da corredor curto em X,
 * travessia em Z e teto caminhavel a 5,18 m - a verticalidade sai da propria
 * carga, nao de predio.
 *
 * PLANTA (visto de cima, +X leste, +Z norte)
 *
 *   z=+41  ############ MURO DO CAIS ############        (agua atras)
 *   z=+40  | apron alto |   CAIS REBAIXADO -1,10 |apron|  guindaste portico
 *   z=+30  |------------ rampas e escada de acesso -----|
 *   z=+15  | FILEIRA 1 (pilhas)  || FILEIRA 1 || FILEIRA 1 |
 *          |   corredor 3,56 m com bloqueio alternado      |
 *   z=+9   | FILEIRA 2 ...                                 |
 *   z=+3   | FILEIRA 3 (o nivel caminhavel continuo)       |
 *   z=-3   | FILEIRA 4 ...                                 |
 *   z=-9   | FILEIRA 5 (pilha de 1 - da para ver por cima) |
 *   z=-18  +-------- GALPAO DE ALFANDEGA (aberto) ---------+
 *   z=-34  |         escritorio no alto (laje 3,5 m)      |
 *   z=-40  ============ via de servico + muro ============
 *
 * ROTA ALTA: escada na faixa central (5,18 m) -> plataforma de chapa -> topo da
 * fileira 3 -> passarelas metalicas sobre as faixas e sobre os corredores ->
 * topo da fileira 1, que domina o cais. A pilha de tres (7,77 m) no meio da
 * fileira 3 corta a rota em duas metades de proposito: sem ela o topo seria uma
 * linha de tiro de 63 m.
 *
 * DESNIVEL DA AGUA: o cais rebaixado fica 1,14 m abaixo do patio e so se entra
 * por duas rampas escalonadas e uma escada central. O muro do cais sobe 3,3 m
 * acima do piso rebaixado, entao ninguem cai na agua.
 */

import * as P from '../Props.js';

// ---------------------------------------------------------------------------
// coordenadas
// ---------------------------------------------------------------------------

const MINX = -40, MAXX = 40, MINZ = -40, MAXZ = 40;
const WALL = 1.4;              // espessura do muro do perimetro

const Y_YARD = 0.04;           // patio de estiva (asfalto)
const Y_DECK = -1.10;          // cais rebaixado (concreto)
const Y_WATER = -1.55;

const QUAY_Z0 = 30;            // onde comeca a faixa do cais
const DECK_X0 = -26, DECK_X1 = 26;

// conteiner padrao
const C_LEN = 6.06, C_W = 2.44, C_H = 2.59;
const TOP2 = C_H * 2;          // 5,18 m - o nivel caminhavel do mapa

/** Centros X das colunas de pilha. Passo 6,20 m: folga de 14 cm entre caixas. */
const COLS = [-32.0, -25.8, -19.6, -6.4, -0.2, 6.0, 19.4, 25.6];
/** Centros Z das fileiras. Passo variavel: corredores de 3,56 m. */
const ROWS = [14.0, 8.0, 2.0, -4.0, -10.0];

/**
 * Quantos conteineres em cada pilha (linha = fileira, coluna = COLS).
 * 2 = topo caminhavel (5,18 m). 3 = parede que corta a rota alta. 1 = pilha
 * baixa, para a fileira do sul nao virar muro cego.
 */
const LEVELS = [
  [2, 3, 2, 3, 2, 2, 3, 2],
  [3, 2, 2, 2, 2, 3, 2, 3],
  [2, 2, 2, 2, 3, 2, 2, 2],
  [2, 3, 2, 2, 2, 2, 3, 2],
  [1, 2, 1, 2, 2, 1, 2, 1]
];

/** Galpao de alfandega (aberto nas laterais). */
const SHED_X0 = -30, SHED_X1 = 2, SHED_Z0 = -34, SHED_Z1 = -18, SHED_H = 7.0;
/** Escritorio de despacho, dentro do galpao. */
const OFF_X0 = -30, OFF_X1 = -23.5, OFF_Z0 = -34, OFF_Z1 = -27, OFF_H = 3.2, OFF_TOP = 3.5;

/** Guindaste portico: pernas sobre o cais rebaixado. */
const CRANE_XW = -9, CRANE_XE = 13, CRANE_ZS = 31.6, CRANE_ZN = 38.0, CRANE_H = 13.4;

export const meta = {
  id: 'porto',
  name: 'PORTO',
  sub: 'DOCA 4 - TERMINAL DE CARGA',
  bounds: { minX: MINX, maxX: MAXX, minZ: MINZ, maxZ: MAXZ },
  ambiente: 'dia'
};

// ---------------------------------------------------------------------------
// ajudantes locais
// ---------------------------------------------------------------------------

/** Uma pilha de `n` conteineres em (cx,cz), comprimento no eixo X. */
function stack(level, cx, cz, n, seed) {
  for (let i = 0; i < n; i++) {
    // variante de material por caixa: pilha inteira do mesmo tom le como bloco
    level.batch.setVariant((seed + i * 3) % 4, seed * 17 + i * 7);
    level._place(P.makeContainer(level.mats, { len: C_LEN, w: C_W, h: C_H }),
      cx, Y_YARD + i * C_H, cz, 0);
  }
  level.batch.clearVariant();
}

/**
 * Conteiner curto atravessado num corredor. E o que impede a linha de tiro de
 * 63 m: sobra ~1 m de passagem numa ponta, entao da para passar mas nao para
 * enfiar a mira de um lado ao outro.
 */
function blocker(level, cx, cz, len, rotY, seed) {
  level.batch.setVariant(seed % 4, seed * 29 + 5);
  level._place(P.makeContainer(level.mats, { len, w: C_W, h: C_H }),
    cx, Y_YARD, cz, rotY);
  level.batch.clearVariant();
}

/**
 * Rampa escalonada entre duas cotas. Degrau de 0,285 m, bem abaixo do
 * STEP_HEIGHT de 0,45 m do jogador - sobe e desce andando, sem pulo.
 * eixo 'x': desce de x0 para x1. eixo 'z': desce de z0 para z1.
 */
function stepRamp(level, axis, a0, a1, b0, b1, yHigh, yLow, steps) {
  const n = steps || 4;
  const da = (a1 - a0) / n;
  const dy = (yHigh - yLow) / n;
  for (let i = 0; i < n; i++) {
    const s0 = a0 + da * i;
    const s1 = a0 + da * (i + 1);
    const top = yHigh - dy * i;
    if (axis === 'x') {
      level._slab('concrete', Math.min(s0, s1), yLow, b0, Math.max(s0, s1), top, b1);
    } else {
      level._slab('concrete', b0, yLow, Math.min(s0, s1), b1, top, Math.max(s0, s1));
    }
  }
}

// ---------------------------------------------------------------------------
// construcao
// ---------------------------------------------------------------------------

export function build(level) {
  buildGround(level);
  buildPerimeter(level);
  buildYard(level);
  buildQuay(level);
  buildCrane(level);
  buildShed(level);
  buildServiceRoad(level);
  buildHighRoute(level);
  buildBackdrop(level);
  return true;
}

function buildGround(level) {
  const b = level.batch;

  // terreno base: nao existe buraco em lugar nenhum, nem fora do muro
  b.setRegion('porto_borda');
  level._floor('gravel', MINX - 14, MINZ - 14, MAXX + 14, MAXZ + 14, 0, 1.4);

  // patio de estiva
  b.setRegion('porto_yard');
  level._floor('asphalt', MINX, MINZ, MAXX, QUAY_Z0, Y_YARD, 0.34);

  // aprons altos das duas pontas da faixa do cais
  b.setRegion('porto_cais');
  level._floor('asphalt', MINX, QUAY_Z0, DECK_X0, MAXZ, Y_YARD, 0.34);
  level._floor('asphalt', DECK_X1, QUAY_Z0, MAXX, MAXZ, Y_YARD, 0.34);
  // cais rebaixado: registrado DEPOIS, entao _groundY devolve -1,10 aqui
  level._floor('concrete', DECK_X0, QUAY_Z0, DECK_X1, MAXZ, Y_DECK, 0.5);

  // agua, atras do muro do cais (decorativa: ninguem alcanca)
  b.setRegion('porto_agua');
  level._floor('water', MINX - 24, MAXZ + WALL, MAXX + 24, MAXZ + 36, Y_WATER, 0.4);

  // pintura de piso do patio: faixa das faixas de caminhao
  b.setRegion('porto_yard');
  for (let z = -34; z < 26; z += 4.4) {
    level._slab('asphalt_line', -13.2, Y_YARD, z, -12.8, Y_YARD + 0.012, z + 2.0,
      { solid: false, cast: false });
    level._slab('asphalt_line', 12.5, Y_YARD, z, 12.9, Y_YARD + 0.012, z + 2.0,
      { solid: false, cast: false });
  }
  // numeracao de baia: risco transversal na boca de cada fileira
  for (let i = 0; i < ROWS.length; i++) {
    const z = ROWS[i] - C_W * 0.5 - 0.5;
    level._slab('asphalt_line', -36.5, Y_YARD, z, -34.5, Y_YARD + 0.012, z + 0.35,
      { solid: false, cast: false });
    level._slab('asphalt_line', 29.5, Y_YARD, z, 31.5, Y_YARD + 0.012, z + 0.35,
      { solid: false, cast: false });
  }
}

/**
 * Muro fechado nos quatro lados. O do norte sobe 2,2 m sobre o patio e 3,3 m
 * sobre o cais rebaixado: nao se pula, entao nao se cai na agua.
 */
function buildPerimeter(level) {
  const b = level.batch;
  b.setRegion('porto_borda');

  level._slab('concrete', MINX - WALL, -0.9, MINZ - WALL, MINX, 5.2, MAXZ + WALL);
  level._slab('concrete', MAXX, -0.9, MINZ - WALL, MAXX + WALL, 5.2, MAXZ + WALL);
  level._slab('concrete', MINX - WALL, -0.9, MINZ - WALL, MAXX + WALL, 4.6, MINZ);
  level._slab('concrete', MINX - WALL, -2.9, MAXZ, MAXX + WALL, 2.2, MAXZ + WALL);

  // coroamento: a aresta viva no alto e o que faz o muro ler como obra
  level._slab('concrete', MINX - WALL - 0.12, 5.2, MINZ - WALL, MINX + 0.12, 5.42, MAXZ + WALL,
    { solid: false });
  level._slab('concrete', MAXX - 0.12, 5.2, MINZ - WALL, MAXX + WALL + 0.12, 5.42, MAXZ + WALL,
    { solid: false });
  level._slab('concrete', MINX - WALL, 4.6, MINZ - WALL - 0.12, MAXX + WALL, 4.82, MINZ + 0.12,
    { solid: false });
  level._slab('concrete', MINX - WALL, 2.2, MAXZ - 0.12, MAXX + WALL, 2.42, MAXZ + WALL + 0.12,
    { solid: false });

  // contrafortes na face de dentro dos muros compridos
  for (let z = MINZ + 3; z < MAXZ - 2; z += 6.5) {
    level._slab('concrete', MINX, -0.5, z, MINX + 0.6, 4.4, z + 0.7, { solid: false });
    level._slab('concrete', MAXX - 0.6, -0.5, z, MAXX, 4.4, z + 0.7, { solid: false });
  }
  for (let x = MINX + 4; x < MAXX - 3; x += 7.0) {
    level._slab('concrete', x, -0.5, MINZ, x + 0.7, 3.9, MINZ + 0.6, { solid: false });
  }

  // portao de servico no muro sul (fechado - so silhueta e leitura de lugar)
  level._slab('metal', -4.4, -0.2, MINZ - 0.1, 4.4, 4.2, MINZ + 0.1);
  for (let i = 0; i < 11; i++) {
    const x = -4.2 + i * 0.82;
    level._slab('metal_thin', x, 0.1, MINZ - 0.16, x + 0.16, 4.0, MINZ - 0.06,
      { solid: false, cast: false });
  }

  // holofotes de patio: a vertical que quebra o muro reto
  for (const px of [-30, 0, 30]) {
    level._place(P.makePole(level.mats, { h: 9.4, transformer: px === 0 }),
      px, Y_YARD, MINZ + 1.6, 0);
  }
  level._place(P.makePole(level.mats, { h: 9.4 }), MINX + 1.7, Y_YARD, 6.0, Math.PI / 2);
  level._place(P.makePole(level.mats, { h: 9.4, transformer: true }),
    MAXX - 1.7, Y_YARD, -6.0, -Math.PI / 2);
}

/** As cinco fileiras de pilha e os bloqueios de corredor. */
function buildYard(level) {
  const b = level.batch;
  b.setRegion('porto_yard');

  for (let r = 0; r < ROWS.length; r++) {
    for (let c = 0; c < COLS.length; c++) {
      stack(level, COLS[c], ROWS[r], LEVELS[r][c], r * 11 + c * 3 + 1);
    }
  }

  /**
   * Bloqueios dos quatro corredores leste-oeste. `flush` -1 encosta na fileira
   * do sul e +1 na do norte: alternando o lado, nem a passagem de 1 m fica
   * alinhada de um corredor para o outro.
   */
  const corr = [
    { z: (ROWS[0] + ROWS[1]) * 0.5, xs: [-31, -8, 18], flush: [-1, 1, -1] },
    { z: (ROWS[1] + ROWS[2]) * 0.5, xs: [-22, 4, 24], flush: [1, -1, 1] },
    { z: (ROWS[2] + ROWS[3]) * 0.5, xs: [-34, -6, 21], flush: [-1, 1, -1] },
    { z: (ROWS[3] + ROWS[4]) * 0.5, xs: [-26, 0, 26], flush: [1, -1, 1] }
  ];
  const half = (3.56 - 2.6) * 0.5;
  for (let i = 0; i < corr.length; i++) {
    const cz = corr[i].z;
    for (let k = 0; k < corr[i].xs.length; k++) {
      blocker(level, corr[i].xs[k], cz + corr[i].flush[k] * half, 2.6, Math.PI / 2,
        i * 7 + k + 2);
    }
  }

  // bloqueios das duas faixas de caminhao (norte-sul)
  blocker(level, -13.4, -14.0, C_LEN, 0, 41);
  blocker(level, -12.6, 22.0, C_LEN, 0, 42);
  blocker(level, 13.2, -14.0, C_LEN, 0, 43);
  blocker(level, 12.2, 22.0, C_LEN, 0, 44);

  // tralha de patio: tambores, paletes e pneu no pe das pilhas
  const rng = P.makeRng(9091);
  const spots = [
    [-35.6, 5.0], [-35.6, -7.2], [-16.0, -12.0], [-9.9, 5.4], [3.0, -13.6],
    [10.0, 11.4], [17.2, -7.0], [28.4, -12.4], [30.6, 17.0], [-30.0, 18.6]
  ];
  for (let i = 0; i < spots.length; i++) {
    const sx = spots[i][0];
    const sz = spots[i][1];
    const r = rng();
    if (r < 0.34) {
      for (let k = 0; k < 3; k++) {
        level._place(P.makeBarrel(level.mats, { variant: k === 2 ? 1 : 0 }),
          sx + P.rand(rng, -0.5, 0.5), Y_YARD, sz + P.rand(rng, -0.5, 0.5), rng() * 3.1);
      }
    } else if (r < 0.62) {
      level._place(P.makePallet(level.mats), sx, Y_YARD, sz, rng() * 3.1);
      level._place(P.makeCrate(level.mats, { variant: 1 }), sx, Y_YARD + 0.11, sz, rng() * 0.7);
      level._place(P.makeCrate(level.mats, { variant: 2, w: 0.7, h: 0.55, d: 0.7 }),
        sx + 0.2, Y_YARD + 0.83, sz - 0.1, rng() * 0.9);
    } else if (r < 0.82) {
      level._place(P.makeDumpster(level.mats, {}), sx, Y_YARD, sz, rng() * 3.1);
    } else {
      for (let k = 0; k < 4; k++) {
        level._place(P.makeTire(level.mats, {}), sx, Y_YARD + k * 0.19, sz, rng() * 3.1);
      }
    }
  }
}

/** Cais rebaixado: acessos, cabecos de amarracao e a carga de bordo. */
function buildQuay(level) {
  const b = level.batch;
  b.setRegion('porto_cais');

  // rampa oeste e rampa leste, dentro da faixa do cais
  stepRamp(level, 'x', DECK_X0, DECK_X0 + 4, 32.5, 37.5, Y_YARD, Y_DECK, 4);
  stepRamp(level, 'x', DECK_X1, DECK_X1 - 4, 32.5, 37.5, Y_YARD, Y_DECK, 4);
  // escada central, descendo para o norte
  stepRamp(level, 'z', QUAY_Z0, QUAY_Z0 + 3.6, -2.2, 2.2, Y_YARD, Y_DECK, 4);

  // guarda-corpo na borda do desnivel, menos onde estao os acessos
  const edges = [[DECK_X0 + 4.2, -2.6], [2.6, DECK_X1 - 4.2]];
  for (let i = 0; i < edges.length; i++) {
    const r = P.makeRailing(level.mats, { len: edges[i][1] - edges[i][0], h: 1.02, mesh: true });
    level._place(r, (edges[i][0] + edges[i][1]) * 0.5, Y_YARD, QUAY_Z0 - 0.35, 0);
  }

  // cabecos de amarracao ao pe do muro
  for (let x = MINX + 4; x < MAXX - 3; x += 7.5) {
    const y = (x > DECK_X0 && x < DECK_X1) ? Y_DECK : Y_YARD;
    level._slab('metal', x - 0.26, y, MAXZ - 1.5, x + 0.26, y + 0.62, MAXZ - 0.98);
    level._slab('metal', x - 0.34, y + 0.62, MAXZ - 1.58, x + 0.34, y + 0.78, MAXZ - 0.9,
      { solid: false });
  }

  // carga de bordo esperando embarque: pilha de dois e caixotes, no rebaixado
  stack(level, -14.0, 33.0, 2, 71);
  stack(level, 8.0, 33.2, 2, 72);
  level._place(P.makeSandbagWall(level.mats, { len: 4.2, rows: 3, rng: P.makeRng(73) }),
    -4.0, Y_DECK, 36.4, 0);
  const rng = P.makeRng(4321);
  for (let i = 0; i < 7; i++) {
    level._place(P.makeCrate(level.mats, { variant: i % 3 }),
      P.rand(rng, -22, 22), Y_DECK, P.rand(rng, 34.5, 39.0), rng() * 3.1);
  }
  for (let i = 0; i < 5; i++) {
    level._place(P.makeBarrel(level.mats, { variant: i === 3 ? 2 : 0 }),
      P.rand(rng, -24, 24), Y_DECK, P.rand(rng, 31.5, 39.2), rng() * 3.1);
  }

  // balsa atracada: da escala ao cais e fecha o quadro atras do muro
  b.setRegion('porto_agua');
  level._slab('metal', -30, Y_WATER - 1.2, 44.0, -4, 1.5, 58.0);
  level._slab('metal_thin', -30, 1.5, 44.0, -4, 1.72, 58.0, { solid: false });
  level._slab('metal_thin', -26, 1.72, 46.0, -8, 2.05, 55.0, { solid: false });
  level._slab('plaster', -14, 1.72, 52.0, -6, 6.4, 57.4, { solid: false });
  level._slab('glass', -13.6, 4.4, 51.8, -6.4, 5.6, 52.1, { solid: false, cast: false });
  level._slab('metal_thin', -11.2, 6.4, 54.0, -8.8, 9.6, 55.4, { solid: false });
  for (let i = 0; i < 6; i++) {
    level._place(P.makeContainer(level.mats, { len: C_LEN, w: C_W, h: C_H }),
      -26 + (i % 3) * 6.6, 1.72, 47.0 + Math.floor(i / 3) * 2.9, 0);
  }
}

/**
 * Guindaste portico sobre o cais. Quatro pernas solidas (cobertura de verdade no
 * rebaixado), viga de rolamento, casa de maquinas e lanca em balanco sobre a
 * agua. Tudo em caixa alinhada: uma trelica de verdade nao paga o custo.
 */
function buildCrane(level) {
  const b = level.batch;
  b.setRegion('porto_cais');

  const legs = [[CRANE_XW, CRANE_ZS], [CRANE_XW, CRANE_ZN], [CRANE_XE, CRANE_ZS], [CRANE_XE, CRANE_ZN]];
  for (let i = 0; i < legs.length; i++) {
    const lx = legs[i][0];
    const lz = legs[i][1];
    level._slab('metal', lx - 0.52, Y_DECK, lz - 0.52, lx + 0.52, CRANE_H, lz + 0.52);
    // sapata e trilho
    level._slab('metal_thin', lx - 0.9, Y_DECK, lz - 0.9, lx + 0.9, Y_DECK + 0.26, lz + 0.9,
      { solid: false });
  }
  // trilho de rolamento no piso do cais
  for (const lz of [CRANE_ZS, CRANE_ZN]) {
    level._slab('metal_thin', DECK_X0 + 1, Y_DECK, lz - 0.12, DECK_X1 - 1, Y_DECK + 0.09, lz + 0.12,
      { solid: false, cast: false });
    // travamento horizontal entre as pernas do mesmo trilho
    level._slab('metal', CRANE_XW - 0.28, 6.2, lz - 0.2, CRANE_XE + 0.28, 6.55, lz + 0.2,
      { solid: false });
    level._slab('metal', CRANE_XW - 0.28, 10.6, lz - 0.2, CRANE_XE + 0.28, 10.95, lz + 0.2,
      { solid: false });
    // viga de rolamento no alto, avancando alem das pernas
    level._slab('metal', CRANE_XW - 4.0, CRANE_H, lz - 0.45, CRANE_XE + 4.0, CRANE_H + 1.2,
      lz + 0.45);
  }
  // travessas do portico, ligando os dois trilhos
  for (const lx of [CRANE_XW, CRANE_XE]) {
    level._slab('metal', lx - 0.3, CRANE_H + 1.2, CRANE_ZS, lx + 0.3, CRANE_H + 1.5, CRANE_ZN,
      { solid: false });
  }
  // casa de maquinas
  level._slab('metal_thin', -1.0, CRANE_H + 1.5, 32.6, 4.4, CRANE_H + 4.2, 37.0);
  level._slab('glass', -1.1, CRANE_H + 2.6, 32.5, 4.5, CRANE_H + 3.6, 32.7,
    { solid: false, cast: false });
  level._slab('metal_thin', -1.4, CRANE_H + 4.2, 32.3, 4.8, CRANE_H + 4.42, 37.3,
    { solid: false });

  // lanca sobre a agua e contra-lanca sobre o patio
  const boomY = CRANE_H + 1.7;
  level._slab('metal', 1.0, boomY, CRANE_ZN, 3.6, boomY + 1.2, 58.0);
  level._slab('metal', 1.0, boomY, 22.0, 3.6, boomY + 1.2, CRANE_ZS);
  level._slab('metal_thin', 1.3, boomY + 1.2, 24.0, 3.3, boomY + 3.6, 26.0, { solid: false });
  // cabo e espalhador pendurados sobre a agua
  level._slab('metal_thin', 2.24, 5.4, 47.4, 2.36, boomY, 47.52, { solid: false, cast: false });
  level._slab('metal_dull', 0.2, 4.6, 45.2, 4.4, 5.4, 49.8, { solid: false });
  // escada de acesso da perna (decorativa: para na plataforma de manutencao)
  level._slab('metal_thin', CRANE_XE + 0.55, Y_DECK, CRANE_ZS - 0.3, CRANE_XE + 0.68, 6.2,
    CRANE_ZS + 0.3, { solid: false, cast: false });
  level._slab('metal_thin', CRANE_XE - 1.6, 6.2, CRANE_ZS - 1.0, CRANE_XE + 1.6, 6.34,
    CRANE_ZS + 1.0, { solid: false });
}

/**
 * Galpao de alfandega ABERTO: cobertura sobre colunas, sem parede lateral. A
 * leitura de combate e diferente do patio - teto alto, muitas colunas, tiro
 * cruzado curto - e o escritorio no alto e a segunda cota do sul do mapa.
 */
function buildShed(level) {
  const b = level.batch;
  b.setRegion('porto_sul');

  // piso de concreto, um degrau acima do patio (doca de carga)
  level._floor('concrete', SHED_X0, SHED_Z0, SHED_X1, SHED_Z1, Y_YARD + 0.28, 0.5);
  stepRamp(level, 'z', SHED_Z1, SHED_Z1 + 1.0, -22.0, -6.0, Y_YARD + 0.28, Y_YARD, 1);
  stepRamp(level, 'x', SHED_X1, SHED_X1 + 1.0, -30.0, -22.0, Y_YARD + 0.28, Y_YARD, 1);

  const yFloor = Y_YARD + 0.28;

  // colunas
  const colX = [-19, -11, -3];
  const colZ = [-32.5, -19.5];
  for (let i = 0; i < colX.length; i++) {
    for (let k = 0; k < colZ.length; k++) {
      level._place(P.makeColumn(level.mats, { h: SHED_H, r: 0.32 }), colX[i], yFloor, colZ[k], 0);
    }
  }
  level._place(P.makeColumn(level.mats, { h: SHED_H, r: 0.32 }), -27, yFloor, -19.5, 0);

  // cobertura: chapa sobre tesouras aparentes
  level._slab('metal_thin', SHED_X0 - 0.7, SHED_H - 0.22, SHED_Z0 - 0.7, SHED_X1 + 0.7,
    SHED_H, SHED_Z1 + 0.7);
  for (let x = SHED_X0; x <= SHED_X1; x += 4.0) {
    level._slab('metal', x - 0.11, SHED_H - 0.74, SHED_Z0 - 0.6, x + 0.11, SHED_H - 0.22,
      SHED_Z1 + 0.6, { solid: false });
  }
  for (const zz of [SHED_Z0 + 3.5, (SHED_Z0 + SHED_Z1) * 0.5, SHED_Z1 - 3.5]) {
    level._slab('metal', SHED_X0 - 0.6, SHED_H - 0.62, zz - 0.13, SHED_X1 + 0.6,
      SHED_H - 0.34, zz + 0.13, { solid: false });
  }
  // beiral de lona nas duas bocas: o galpao aberto ganha sombra rasgada no chao
  level._place(P.makeAwning(level.mats, { w: 8.0, d: 2.2, drop: 0.8, rng: P.makeRng(55) }),
    -8.0, SHED_H - 0.3, SHED_Z1 + 0.7, 0);
  level._place(P.makeAwning(level.mats, { w: 7.0, d: 2.0, drop: 0.7, rng: P.makeRng(56) }),
    -14.0, SHED_H - 0.3, SHED_Z0 - 0.7, Math.PI);
  level._place(P.makeSign(level.mats, { w: 4.2, h: 0.8 }), -10.0, SHED_H - 1.5, SHED_Z1 + 0.5, 0);

  // --- escritorio de despacho, com vidro para o galpao --------------------
  const t = 0.28;
  level._slab('plaster', OFF_X0, yFloor, OFF_Z0, OFF_X1, OFF_H, OFF_Z0 + t);
  level._slab('plaster', OFF_X0, yFloor, OFF_Z0 + t, OFF_X0 + t, OFF_H, OFF_Z1 - t);
  // face norte: peitoril, vidro corrido e viga
  level._slab('plaster', OFF_X0, yFloor, OFF_Z1 - t, OFF_X1, yFloor + 1.0, OFF_Z1);
  level._slab('glass', OFF_X0 + 0.1, yFloor + 1.0, OFF_Z1 - 0.16, OFF_X1 - 0.1, yFloor + 2.3,
    OFF_Z1 - 0.06, { cast: false });
  level._slab('plaster', OFF_X0, yFloor + 2.3, OFF_Z1 - t, OFF_X1, OFF_H, OFF_Z1);
  // face leste: porta (vao real) e vidro ao lado
  level._slab('plaster', OFF_X1 - t, yFloor, OFF_Z0 + t, OFF_X1, OFF_H, -31.6);
  level._slab('plaster', OFF_X1 - t, yFloor + 2.25, -31.6, OFF_X1, OFF_H, -30.1);
  level._slab('plaster', OFF_X1 - t, yFloor, -30.1, OFF_X1, yFloor + 1.0, OFF_Z1 - t);
  level._slab('glass', OFF_X1 - 0.16, yFloor + 1.0, -30.0, OFF_X1 - 0.06, yFloor + 2.3,
    OFF_Z1 - t, { cast: false });
  level._slab('plaster', OFF_X1 - t, yFloor + 2.3, -30.1, OFF_X1, OFF_H, OFF_Z1 - t);
  level._place(P.makeDoorUnit(level.mats, { w: 1.3, h: 2.2, style: 'metal', ajar: 0.7, open: true }),
    OFF_X1 - t * 0.5, yFloor, -30.85, Math.PI / 2);
  // laje do escritorio: e por ela que se domina o galpao
  level._slab('concrete', OFF_X0 - 0.15, OFF_H, OFF_Z0 - 0.15, OFF_X1 + 0.15, OFF_TOP,
    OFF_Z1 + 0.15);
  level._place(P.makeRailing(level.mats, { len: OFF_X1 - OFF_X0 - 0.4, h: 1.0, mesh: true }),
    (OFF_X0 + OFF_X1) * 0.5, OFF_TOP, OFF_Z1 - 0.2, 0);
  level._place(P.makeSandbagWall(level.mats, { len: 3.2, rows: 3, rng: P.makeRng(57) }),
    OFF_X1 - 1.4, OFF_TOP, OFF_Z0 + 2.6, Math.PI / 2);
  level._place(P.makeCrate(level.mats, { variant: 1 }), OFF_X0 + 1.4, OFF_TOP, OFF_Z0 + 1.5, 0.5);

  // escada externa para a laje do escritorio (sobe em +Z)
  const rise = (OFF_TOP - yFloor) / 10;
  level._place(P.makeStairs(level.mats, { steps: 10, rise, run: 0.4, w: 1.6, landing: 1.2 }),
    -22.6, yFloor, OFF_Z0, 0);

  // interior do escritorio: bancada e lampada
  level._place(P.makeWorkbench(level.mats, { w: 2.2 }), OFF_X0 + 1.6, yFloor, OFF_Z0 + 1.2, 0);
  level._place(P.makeHangingLamp(level.mats, { len: 0.9 }), -26.5, OFF_H - 0.1, -30.5, 0);

  // --- carga dentro do galpao --------------------------------------------
  const rng = P.makeRng(6161);
  for (let i = 0; i < 6; i++) {
    const sx = -21.0 + i * 3.8;
    level._place(P.makeShelf(level.mats, { w: 2.4, h: 2.4, d: 0.8 }), sx, yFloor, -31.0, 0);
    level._place(P.makeCrate(level.mats, { variant: i % 2, w: 0.8, h: 0.6, d: 0.7 }),
      sx, yFloor + 1.45, -31.0, P.rand(rng, -0.3, 0.3));
  }
  for (let i = 0; i < 8; i++) {
    const px = P.rand(rng, SHED_X0 + 2, SHED_X1 - 2);
    const pz = P.rand(rng, SHED_Z0 + 3, SHED_Z1 - 2);
    if (px < OFF_X1 + 1 && pz < OFF_Z1 + 1) continue;
    level._place(P.makePallet(level.mats), px, yFloor, pz, rng() * 3.1);
    level._place(P.makeCrate(level.mats, { variant: i % 4 }), px, yFloor + 0.11, pz,
      rng() * 3.1);
  }
  level._place(P.makeTarpStack(level.mats, { count: 3, rng: P.makeRng(58) }), -6.0, yFloor, -30.0, 0.4);
  level._place(P.makeDumpster(level.mats, {}), 0.0, yFloor, -21.0, Math.PI / 2);
  level._interiorDust(SHED_X0, SHED_Z0, SHED_X1, SHED_Z1, yFloor, 1.4);
}

/** Via de servico ao sul: veiculos, barreiras e a guarita. */
function buildServiceRoad(level) {
  const b = level.batch;
  b.setRegion('porto_sul');

  level._place(P.makeVehicle(level.mats, { kind: 'pickup', burned: false, doorOpen: true }),
    -20.0, Y_YARD, -37.2, 0.08);
  level._place(P.makeVehicle(level.mats, { kind: 'sedan', burned: true }),
    16.0, Y_YARD, -37.6, Math.PI - 0.2);
  level._place(P.makeVehicle(level.mats, { kind: 'pickup' }), 33.0, Y_YARD, -30.0, Math.PI / 2);

  for (let i = 0; i < 5; i++) {
    level._place(P.makeJerseyBarrier(level.mats, { len: 2.4 }),
      6.0 + i * 2.6, Y_YARD, -35.2, 0);
  }
  for (let i = 0; i < 3; i++) {
    level._place(P.makeJerseyBarrier(level.mats, { len: 2.4 }),
      -33.0, Y_YARD, -30.0 + i * 2.6, Math.PI / 2);
  }

  // guarita
  level._slab('plaster', 24.0, Y_YARD, -38.6, 28.4, 2.9, -34.6);
  level._slab('glass', 23.9, Y_YARD + 1.0, -38.7, 24.1, Y_YARD + 2.1, -34.5,
    { solid: false, cast: false });
  level._slab('glass', 24.0, Y_YARD + 1.0, -34.7, 28.4, Y_YARD + 2.1, -34.5,
    { solid: false, cast: false });
  level._slab('concrete', 23.7, 2.9, -38.9, 28.7, 3.15, -34.3);
  level._place(P.makeAC(level.mats, {}), 26.2, 2.0, -34.4, 0);

  // tanques de combustivel: silhueta pesada no canto sudeste
  for (let i = 0; i < 2; i++) {
    level._place(P.makeWaterTank(level.mats, { r: 1.9, h: 3.4, legs: false }),
      34.5, Y_YARD, -13.0 + i * 5.2, 0.3);
  }
  level._slab('metal_thin', 32.2, Y_YARD, -16.2, 36.8, Y_YARD + 0.9, -5.4, { solid: false });
  level._place(P.makeRubblePile(level.mats, { r: 1.6, h: 0.5, rng: P.makeRng(77), count: 16 }),
    MINX + 3.4, Y_YARD, -24.0, 0);
}

/**
 * A rota alta: escada -> plataforma de chapa -> topo das pilhas, com passarelas
 * cruzando faixas e corredores. Sem isso o topo dos conteineres seria enfeite.
 */
function buildHighRoute(level) {
  const b = level.batch;
  b.setRegion('porto_yard');

  // escada na faixa central, 14 degraus de 0,37 m
  const steps = 14;
  const rise = (TOP2 - Y_YARD) / steps;
  level._place(P.makeStairs(level.mats, { steps, rise, run: 0.32, w: 1.7, landing: 1.2 }),
    12.7, Y_YARD, -2.2, 0);
  // plataforma de chapa ligando o patamar ao topo da pilha da coluna 6
  level._slab('metal', 11.5, TOP2 - 0.14, 2.1, 16.5, TOP2, 3.7);
  level._place(P.makeRailing(level.mats, { len: 4.8, h: 1.0, mesh: true }),
    14.0, TOP2, 2.16, 0);

  // passarelas: [x, z, comprimento, rotY]
  const walks = [
    [-13.0, 2.0, 7.6, Math.PI / 2],     // sobre a faixa oeste, fileira 3
    [12.7, 2.0, 7.8, Math.PI / 2],      // sobre a faixa leste, fileira 3
    [-25.8, 5.0, 3.9, 0],               // fileira 3 -> fileira 2
    [-19.6, 11.0, 3.9, 0],              // fileira 2 -> fileira 1 (domina o cais)
    [-6.4, -1.0, 3.9, 0],               // desvio pela fileira 4 (metade oeste)
    [6.0, -1.0, 3.9, 0]                 // desvio pela fileira 4 (metade leste)
  ];
  for (let i = 0; i < walks.length; i++) {
    level._place(P.makeCatwalk(level.mats, { len: walks[i][2], w: 1.35 }),
      walks[i][0], TOP2, walks[i][1], walks[i][3]);
  }

  // cobertura no topo: sem isso o nivel alto e um tabuleiro liso
  const tops = [
    [-32.0, 2.0, 0], [-19.6, 2.0, Math.PI / 2], [-0.2, -4.0, 0],
    [19.4, 2.0, Math.PI / 2], [25.6, 2.0, 0], [-19.6, 14.0, 0], [-25.8, 8.0, Math.PI / 2]
  ];
  for (let i = 0; i < tops.length; i++) {
    level._place(P.makeSandbagWall(level.mats, { len: 3.4, rows: 3, rng: P.makeRng(300 + i) }),
      tops[i][0], TOP2, tops[i][1], tops[i][2]);
  }
  level._place(P.makeCrate(level.mats, { variant: 1 }), -30.2, TOP2, 1.2, 0.4);
  level._place(P.makeCrate(level.mats, { variant: 3 }), 24.0, TOP2, 2.8, 0.9);
  level._place(P.makeTarpStack(level.mats, { count: 2, rng: P.makeRng(311) }), -21.6, TOP2, 14.6, 0.2);
  level._place(P.makeBarrel(level.mats, { variant: 1 }), 6.6, TOP2, -4.6, 0);
  level._place(P.makeBarrel(level.mats, {}), 5.4, TOP2, -3.4, 0);
}

/**
 * Cenografia de fundo: massa de cidade portuaria fora do muro. Material
 * 'backdrop' (claro e dessaturado) na regiao 'fundo' - nao projeta sombra, nao
 * colide e sai em poucos bins.
 */
function buildBackdrop(level) {
  const b = level.batch;
  b.setRegion('fundo');
  const off = { solid: false, cast: false };

  /**
   * [x0, z0, x1, z1, altura, eixo da face voltada para o mapa].
   * O eixo vem escrito na tabela em vez de deduzido: massa larga em X tem que
   * mostrar a grade na face Z, e massa larga em Z na face X - deduzir pelo sinal
   * poe a grade de janela na face que ninguem ve.
   */
  const masses = [
    [-70, -80, -40, -50, 16, 'x'], [-34, -86, -4, -52, 22, 'x'], [4, -78, 34, -48, 13, 'x'],
    [46, -60, 78, -26, 18, 'z'], [48, -8, 80, 26, 24, 'z'], [-84, -14, -50, 18, 20, 'z'],
    [-80, 26, -46, 58, 15, 'z'], [46, 30, 76, 60, 19, 'z']
  ];
  for (let i = 0; i < masses.length; i++) {
    const m = masses[i];
    b.setPhase(i * 19 + 3);
    level._slab('backdrop', m[0], 0, m[1], m[2], m[4], m[3], off);
    b.clearVariant();
    level._slab('concrete', m[0] - 0.3, m[4], m[1] - 0.3, m[2] + 0.3, m[4] + 0.9, m[3] + 0.3, off);
    if (m[5] === 'x') {
      // face num plano Z: a que olha para o mapa e a de coordenada maior
      level._blindWindows({ axis: 'x', face: m[3], sgn: 1, u0: m[0] + 1, u1: m[2] - 1,
        y1: m[4] - 1, seed: i * 331 + 7, opts: off });
    } else {
      const west = m[0] > 0;
      level._blindWindows({ axis: 'z', face: west ? m[0] : m[2], sgn: west ? -1 : 1,
        u0: m[1] + 1, u1: m[3] - 1, y1: m[4] - 1, seed: i * 331 + 7, opts: off });
    }
  }

  // silos de graneis: a silhueta que diz "porto" de longe
  for (let i = 0; i < 6; i++) {
    const sx = -22 + i * 8.4;
    level._slab('backdrop', sx - 3.6, 0, 64, sx + 3.6, 26 + (i % 3) * 3, 71.2, off);
    level._slab('concrete', sx - 4.0, 26 + (i % 3) * 3, 63.6, sx + 4.0, 27.4 + (i % 3) * 3,
      71.6, off);
  }
  // ponte transportadora ligando os silos ao cais
  level._slab('metal_thin', -26, 21, 66.0, 30, 23.2, 68.0, off);

  // dois guindastes de porto no horizonte, um de cada lado
  for (let k = 0; k < 2; k++) {
    const gx = k === 0 ? -58 : 62;
    const gz = 52 + k * 9;
    level._slab('metal_thin', gx - 1.1, 0, gz - 1.1, gx + 1.1, 34, gz + 1.1, off);
    level._slab('metal_thin', gx - 1.4, 34, gz - 0.9, gx + 26, 36.2, gz + 0.9, off);
    level._slab('metal_thin', gx - 12, 34.2, gz - 0.9, gx - 1.4, 35.8, gz + 0.9, off);
  }
  // navio no horizonte
  level._slab('backdrop', 8, -1.6, 88, 62, 6.5, 100, off);
  level._slab('plaster', 44, 6.5, 90, 58, 15.5, 98, off);
  for (let i = 0; i < 5; i++) {
    level._slab('backdrop', 12 + i * 6.5, 6.5, 90.5, 17 + i * 6.5, 11.5, 97.5, off);
  }
  // guarda: a chamine do navio, so para nao ficar so caixa reta
  level._slab('metal_thin', 49, 15.5, 92.5, 53, 21, 95.5, off);
  b.setRegion('porto_yard');
}

// ---------------------------------------------------------------------------
// nascimentos e pontos de vista
// ---------------------------------------------------------------------------

/**
 * Todos os pontos foram escolhidos em vao livre e a altura sai de _groundY, a
 * mesma funcao que o Level usa. Os dois pontos de topo passam a cota explicita
 * (5,18 m + 8 cm): _groundY so conhece piso registrado por _floor, e o topo do
 * conteiner nao e piso.
 */
export function spawnPoints(level) {
  const top = TOP2 + 0.08;
  return {
    player: [
      level._spawn(-37.0, -37.0),      // via de servico, canto sudoeste
      level._spawn(-6.0, -37.5),       // via de servico, portao
      level._spawn(24.0, -36.5),       // via de servico, leste
      level._spawn(-13.0, -24.0),      // dentro do galpao de alfandega
      level._spawn(34.0, -20.0),       // apron leste, junto aos tanques
      level._spawn(-37.5, -10.0),      // apron oeste
      level._spawn(12.7, -24.0),       // boca sul da faixa leste
      level._spawn(30.0, 6.0)          // apron leste, meio
    ],
    enemy: [
      level._spawn(-34.0, 35.5),       // apron alto noroeste
      level._spawn(34.0, 35.5),        // apron alto nordeste
      level._spawn(0.0, 35.5),         // cais rebaixado, centro
      level._spawn(-19.0, 35.0),       // cais rebaixado, oeste
      level._spawn(19.0, 34.0),        // cais rebaixado, leste
      level._spawn(-13.0, 18.5),       // boca norte da faixa oeste
      level._spawn(12.7, 18.5),        // boca norte da faixa leste
      level._spawn(0.0, 4.9),          // corredor entre as fileiras 2 e 3
      level._spawn(-19.6, 14.0, top),  // topo da pilha que domina o cais
      level._spawn(25.6, 2.0, top)     // topo da pilha leste
    ]
  };
}

/**
 * `pos` e o contrato do plano; `position` acompanha porque o DebugApi.goto le
 * esse nome. Sao o MESMO array - nao ha duas verdades.
 */
function vp(out, name, fx, fy, fz, tx, ty, tz) {
  const dx = tx - fx;
  const dy = ty - fy;
  const dz = tz - fz;
  const yaw = Math.round((Math.atan2(-dx, -dz) * 180) / Math.PI * 10) / 10;
  const pitch = Math.round((Math.atan2(dy, Math.hypot(dx, dz)) * 180) / Math.PI * 10) / 10;
  const pos = [fx, fy, fz];
  out.push({ name, pos, position: pos, yaw, pitch });
}

export function viewpoints(level) {
  const eye = 1.65;
  const out = [];
  vp(out, 'porto_corredor_estiva', -30.0, Y_YARD + eye, 4.9, 26.0, 1.6, 4.9);
  vp(out, 'porto_faixa_oeste', -13.0, Y_YARD + eye, -26.0, -13.0, 3.0, 26.0);
  vp(out, 'porto_topo_pilha', -19.6, TOP2 + eye, 13.0, 6.0, 0.4, 34.0);
  vp(out, 'porto_cais_rebaixado', 0.0, Y_DECK + eye, 33.0, -18.0, 1.2, 38.0);
  vp(out, 'porto_guindaste', 4.0, Y_DECK + eye, 24.0, 2.4, 14.0, 34.0);
  vp(out, 'porto_galpao_interior', -8.0, Y_YARD + 0.28 + eye, -21.0, -28.0, 1.4, -32.0);
  vp(out, 'porto_escritorio_laje', -26.0, OFF_TOP + eye, -28.0, 0.0, 1.2, -22.0);
  vp(out, 'porto_escada_alta', 12.7, Y_YARD + eye, -6.0, 13.6, 5.6, 3.0);
  vp(out, 'porto_muro_cais', -6.0, Y_DECK + eye, 36.0, 30.0, 6.0, 39.0);
  vp(out, 'porto_plano_geral', -37.0, 16.0, -37.0, 4.0, 1.0, 14.0);
  vp(out, 'porto_plano_geral_norte', 36.0, 15.0, 37.0, -6.0, 1.0, -8.0);
  vp(out, 'porto_conteiner_close', -30.0, 1.5, 4.4, -30.0, 1.45, 3.0);
  return out;
}
